import { action, observable, makeObservable, runInAction } from "mobx";
import ProjectAPI from "../api/ProjectAPI";
import { ProjectRequest } from "../../../models/Project/Project";
import { ProjectRequestFilters } from "../../../models/Project/ProjectRequestFilters";
import { FilterValue } from "../../../models/Filters/FilterValue";

export class ProjectStore {
  projectAPI: ProjectAPI;
  limit: number;
  total_pages: number;
  total_count: number;
  projectRequest: ProjectRequest[];
  ProjectAPI: any;
  orderType: FilterValue[];
  shipmentType: FilterValue[];
  shipmentModes: FilterValue[];
  paymentStatuses: FilterValue[];

  constructor() {
    this.projectAPI = new ProjectAPI();
    this.projectRequest = [];
    this.limit = 0;
    this.total_pages = 0;
    this.total_count = 0;
    this.orderType = [];
    this.shipmentType = [];
    this.shipmentModes = [];
    this.paymentStatuses = [];

    makeObservable(this, {
      projectRequest: observable,
      getProjectRequestList: action,
      getProjectRequestsByOrderId: action,
      approveRequest: action,
      financeApproveRequest: action,
      financeRejectRequest: action,
      rejectRequest: action,
      raiseProjectRequest: action,
      orderType: observable,
      shipmentType: observable,
      shipmentModes: observable,
      paymentStatuses: observable,
    });
  }

  private applyProjectRequestUpdate = (
    id: number,
    patch: Partial<ProjectRequest>,
  ) => {
    const requestIndex = this.projectRequest.findIndex((req) => req.id === id);
    if (requestIndex === -1) return;

    const current = this.projectRequest[requestIndex];
    const next = { ...current, ...patch };

    this.projectRequest = [
      ...this.projectRequest.slice(0, requestIndex),
      next,
      ...this.projectRequest.slice(requestIndex + 1),
    ];
  };

  getProjectRequestList = async (params: ProjectRequestFilters) => {
    try {
      const response = await this.projectAPI.getProjectRequestList(params);
      this.limit = response.limit;
      this.total_pages = response.total_pages;
      this.total_count = response.total_count;
      this.projectRequest = response.list;
    } catch (error: any) {
      throw error;
    }
  };

  getProjectRequestsByOrderId = async (orderId: number) => {
    try {
      const response = await this.projectAPI.getRequestsByOrderId(orderId);
      runInAction(() => {
        this.projectRequest = response.result || [];
      });
    } catch (error: any) {
      throw error;
    }
  };

  approveRequest = async (
    id: number,
    closerId?: number,
    closerName?: string
  ) => {
    try {
      const res = await this.projectAPI.approveRequest(id);

      if (res.success) {
        runInAction(() => {
          this.applyProjectRequestUpdate(id, {
            status: 2,
            status_label: "Approved",
            closed_on: new Date().toISOString(),
            closer_id: closerId,
            closer_name: closerName,
          });
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  financeApproveRequest = async (id: number) => {
    try {
      const res = await this.projectAPI.financeApproveRequest(id);

      if (res.success) {
        runInAction(() => {
          const updated = res.result as ProjectRequest | undefined;
          if (!updated) return;
          const current = this.projectRequest.find((req) => req.id === id);
          if (!current) return;
          this.applyProjectRequestUpdate(id, { ...current, ...updated });
        });
      }
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  financeRejectRequest = async (id: number, rejectionReason: string) => {
    try {
      const res = await this.projectAPI.financeRejectRequest(id, rejectionReason);

      if (res.success) {
        runInAction(() => {
          const updated = res.result as ProjectRequest | undefined;
          if (!updated) return;
          const current = this.projectRequest.find((req) => req.id === id);
          if (!current) return;
          this.applyProjectRequestUpdate(id, { ...current, ...updated });
        });
      }
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  rejectRequest = async (id: number, cancelReason: string) => {
    try {
      const res = await this.projectAPI.rejectRequest(id, cancelReason);

      if (res.success) {
        runInAction(() => {
          this.applyProjectRequestUpdate(id, {
            status: 3,
            status_label: "Cancelled",
            cancel_reason: cancelReason,
          });
        });
      }
    } catch (error: any) {
      throw error;
    }
  };

  getTypeOfOrder = async () => {
    try {
      const res = await this.projectAPI.getTypeOfOrder();
      if (res?.success) {
        this.orderType = res.result?.map(
          (item: { id: number; type: string }) => ({
            id: item.id.toString(),
            value: item.type,
          })
        );
      }
    } catch (error: any) {
      throw error;
    }
  };

  getTypeOfShipments = async () => {
    try {
      const res = await this.projectAPI.getTypeOfShipments();
      if (res?.success) {
        this.shipmentType = res.result?.map(
          (item: { id: number; type: string }) => ({
            id: item.id.toString(),
            value: item.type,
          })
        );
      }
    } catch (error: any) {
      throw error;
    }
  };

  getModeOfShipments = async () => {
    try {
      const res = await this.projectAPI.getModeOfShipments();
      if (res?.success) {
        this.shipmentModes = res.result?.map(
          (item: { id: number; mode: string }) => ({
            id: item.id.toString(),
            value: item.mode,
          })
        );
      }
    } catch (error: any) {
      throw error;
    }
  };

  getPaymentStatuses = async () => {
    try {
      const res = await this.projectAPI.getPaymentStatuses();
      if (res?.success) {
        this.paymentStatuses = res.result?.map(
          (item: { id: number; status: string }) => ({
            id: item.id.toString(),
            value: item.status,
          })
        );
      }
    } catch (error: any) {
      throw error;
    }
  };

  raiseProjectRequest = async (data: Partial<ProjectRequest>) => {
    try {
      const res = await this.projectAPI.raiseProjectRequest(data);
      if (res?.success) {
        this.projectRequest.push(res.result);
      }
      return res;
    } catch (error: any) {
      throw error;
    }
  };

  createDefaultTasks = async (orderId: number) => {
    try {
      await this.projectAPI.createDefaultTasks(orderId);
    } catch (error: any) {
      throw error;
    }
  };
}
